import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Layout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const logout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `px-4 py-2 rounded-lg text-sm border transition-all duration-150 ${
      isActive
        ? 'text-white border-purple-500/60 bg-purple-500/15'
        : 'text-white/50 border-white/10 hover:text-pink-400 hover:border-pink-500/40 hover:bg-pink-500/08'
    }`;

  return (
    <div className="min-h-screen" style={{ background: '#3e3a52', position: 'relative' }}>
      <div style={{ position: 'fixed', top: 0, left: 0, bottom: 0, width: '3px', background: 'linear-gradient(180deg, transparent, rgba(185,106,242,0.5) 30%, rgba(219,18,202,0.4) 70%, transparent)', pointerEvents: 'none', zIndex: 100 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '3px', background: 'linear-gradient(180deg, transparent, rgba(185,106,242,0.5) 30%, rgba(219,18,202,0.4) 70%, transparent)', pointerEvents: 'none', zIndex: 100 }} />
      <nav style={{ background: '#2e2a40', borderBottom: '1px solid rgba(185,106,242,0.12)' }}
        className="flex items-center h-16 px-6 gap-3">

        <div className="flex items-center gap-2 mr-auto">
          <div className="w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center"
            style={{ borderColor: '#b96af2', boxShadow: '0 0 7px rgba(185,106,242,0.5)' }}>
            <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#b96af2' }} />
          </div>
          <span className="text-sm font-medium" style={{ color: '#b96af2' }}>Duffarn</span>
        </div>

        <NavLink to="/dashboard" end className={navClass}>{t('nav.dashboard')}</NavLink>
        <NavLink to="/dashboard/tasks" className={navClass}>{t('nav.tasks')}</NavLink>
        <NavLink to="/agents" className={navClass}>{t('nav.agents')}</NavLink>
        <NavLink to="/library" className={navClass}>{t('nav.library')}</NavLink>

        <div className="relative ml-2" ref={ref}>
          <button onClick={() => setOpen(o => !o)}
            className="w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all duration-150"
            style={{
              borderColor: open ? '#b96af2' : 'rgba(185,106,242,0.45)',
              background: open ? 'rgba(185,106,242,0.18)' : 'rgba(185,106,242,0.08)',
              boxShadow: open ? '0 0 12px rgba(185,106,242,0.5)' : '0 0 6px rgba(185,106,242,0.25)'
            }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="#b96af2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="8" r="4"/>
              <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
            </svg>
          </button>

          {open && (
            <div className="absolute right-0 top-10 rounded-xl p-2 z-50 min-w-44"
              style={{ background: '#2a2540', border: '1px solid rgba(185,106,242,0.25)', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
              <div className="text-xs px-2.5 pb-2 pt-1"
                style={{ color: 'rgba(255,255,255,0.25)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                petter
              </div>
              <button onClick={() => { navigate('/settings'); setOpen(false); }}
                className="flex items-center gap-2.5 w-full text-left px-2.5 py-2 rounded-lg text-sm transition-all duration-120"
                style={{ color: 'rgba(255,255,255,0.6)' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(185,106,242,0.12)'; (e.currentTarget as HTMLElement).style.color = '#b96af2'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = ''; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.6)'; }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l-.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                </svg>
                {t('nav.settings')}
              </button>
              <div style={{ height: '1px', background: 'rgba(185,106,242,0.12)', margin: '4px 0' }} />
              <button onClick={logout}
                className="flex items-center gap-2.5 w-full text-left px-2.5 py-2 rounded-lg text-sm transition-all duration-120"
                style={{ color: 'rgba(255,255,255,0.6)' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(219,18,202,0.1)'; (e.currentTarget as HTMLElement).style.color = '#db12ca'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = ''; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.6)'; }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
                {t('nav.logout')}
              </button>
            </div>
          )}
        </div>
      </nav>

      <main className="p-6">
        <Outlet />
      </main>
    </div>
  );
}
